import React, { useEffect, useState } from 'react';
import { Table, Button, message, DatePicker, TimePicker, InputNumber, Form, Modal, Switch, Row, Col, Divider } from 'antd';
import axios from 'axios';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { ExclamationCircleOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { loginRequest } from '../../authConfig';
import { useMsal } from '@azure/msal-react';

dayjs.extend(utc);

const { confirm } = Modal;

const API_URL = process.env.REACT_APP_API_URL;

interface QuarterInfo {
  id: string;
  quarter: number;
  start_date: string;
  end_date: string;
  // Set once the quarter's manager budget has been allocated / de-allocated.
  budget_allocated_at?: string | null;
  budget_deallocated_at?: string | null;
}

const PeopleManagerBudgetAutomation: React.FC = () => {
  const [quarters, setQuarters] = useState<QuarterInfo[]>([]);
  const [selectedStartDate, setSelectedStartDate] = useState<Dayjs | null>(null);
  const [selectedEndDate, setSelectedEndDate] = useState<Dayjs | null>(null);
  const [isAllocateBudget, setIsAllocateBudget] = useState<boolean>(false);
  const [isDeAllocateBudget, setIsDeAllocateBudget] = useState<boolean>(false);

  const [quarterNumber, setQuarterNumber] = useState<number | null>(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [allocatingId, setAllocatingId] = useState<string | null>(null);
  const [deallocatingId, setDeallocatingId] = useState<string | null>(null);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [editingQuarter, setEditingQuarter] = useState<QuarterInfo | null>(null);
  const [editStartDate, setEditStartDate] = useState<Dayjs | null>(null);
  const [editEndDate, setEditEndDate] = useState<Dayjs | null>(null);
  const { instance, accounts } = useMsal();
  const user_id = localStorage.getItem("UserId");

  const fetchToken = async () => {
    const res = await instance.acquireTokenSilent({
      ...loginRequest,
      account: accounts[0],
    });
    return res.idToken;
  };

  const fetchQuarters = async () => {
    try {
      const token = await fetchToken();
      const response = await axios.get(`${API_URL}/api/quarterinfo`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setQuarters(response.data);
      console.log('Fetched quarters:', response.data);
    } catch (error) {
      console.error('Error fetching quarters:', error);
      message.error('Failed to fetch quarters.');
    }
  };

  useEffect(() => {
    fetchQuarters();
  }, []);

  const handleAddQuarter = async () => {
    // Validate quarter number
    if (!quarterNumber || quarterNumber < 1 || quarterNumber > 4 || !Number.isInteger(quarterNumber)) {
      message.error('Please enter a valid quarter number (1, 2, 3, or 4).');
      return;
    }

    // Validate start date
    if (!selectedStartDate) {
      message.error('Please select a start date.');
      return;
    }

    // Validate end date
    if (!selectedEndDate) {
      message.error('Please select an end date.');
      return;
    }

    // Validate that end date is greater than start date
    if (selectedStartDate.isAfter(selectedEndDate) || selectedStartDate.isSame(selectedEndDate)) {
      message.error('End date must be greater than start date.');
      return;
    }

    const payload = {
      quarter: quarterNumber,
      start_date: selectedStartDate.format('YYYY-MM-DD'),
      end_date: selectedEndDate.format('YYYY-MM-DD'),
      isAllocateBudget: isAllocateBudget,
      isDeAllocateBudget: isDeAllocateBudget,
      createdBy: user_id,
    };


    try {
      const token = await fetchToken();
      await axios.post(`${API_URL}/api/quarterinfo`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      message.success('Quarter added successfully.');
      fetchQuarters();
      resetFormFields();
      setIsModalVisible(false);
    } catch (error) {
      console.error('Error adding quarter:', error);
      message.error('Failed to add quarter.');
    }
  };

  const handleCancelModal = () => {
    if (selectedStartDate || selectedEndDate || quarterNumber || isAllocateBudget || isDeAllocateBudget) {
      confirm({
        title: 'Are you sure you want to close?',
        icon: <ExclamationCircleOutlined />,
        content: 'You have unsaved changes. Closing the modal will discard them.',
        okText: 'Yes',
        cancelText: 'No',
        onOk: () => {
          resetFormFields();
          setIsModalVisible(false);
        },
      });
    } else {
      resetFormFields();
      setIsModalVisible(false);
    }
  };

  const resetFormFields = () => {
    setSelectedStartDate(null);
    setSelectedEndDate(null);
    setQuarterNumber(null);
    setIsAllocateBudget(false);
    setIsDeAllocateBudget(false);
  };

  const handleStartService = async () => {
    try {
      const token = await fetchToken();
      await axios.post(
        `${API_URL}/api/backgroundservice/startPeopleManagerBudgetAllocation`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      message.success('People Manager Budget Allocation Service started.');
    } catch (error) {
      console.error('Error starting service:', error);
      message.error('Failed to start service.');
    }
  };

  const handleStopService = async () => {
    try {
      const token = await fetchToken();
      await axios.post(
        `${API_URL}/api/backgroundservice/stopPeopleManagerBudgetAllocation`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      message.success('People Manager Budget Allocation Service stopped.');
    } catch (error) {
      console.error('Error stopping service:', error);
      message.error('Failed to stop service.');
    }
  };

  const handleDeleteQuarter = async (quarterId: string) => {
    confirm({
      title: 'Are you sure you want to delete this quarter?',
      icon: <ExclamationCircleOutlined />,
      content: 'This action cannot be undone.',
      okText: 'Yes, Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          const token = await fetchToken();
          await axios.delete(`${API_URL}/api/quarterinfo/${quarterId}/${user_id}`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });
          message.success('Quarter deleted successfully.');
          fetchQuarters();
        } catch (error) {
          console.error('Error deleting quarter:', error);
          message.error('Failed to delete quarter.');
        }
      },
    });
  };

  const handleAllocateBudgetToggle = async (quarterId: string, currentValue: boolean) => {
    try {
      const token = await fetchToken();
      await axios.post(
        `${API_URL}/api/quarterinfo/updateAllocateBudget/${quarterId}`,
        {
          isAllocateBudget: !currentValue,
          modifiedBy: user_id,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      message.success(`Budget allocation ${!currentValue ? 'enabled' : 'disabled'} successfully.`);
      fetchQuarters();
    } catch (error) {
      console.error('Error updating allocate budget:', error);
      message.error('Failed to update budget allocation.');
    }
  };

  const handleEditQuarter = (record: QuarterInfo) => {
    setEditingQuarter(record);
    setEditStartDate(dayjs(record.start_date));
    setEditEndDate(dayjs(record.end_date));
    setIsEditModalVisible(true);
  };

  const handleEditSave = async () => {
    if (!editStartDate || !editEndDate) {
      message.error('Please select both start and end dates.');
      return;
    }
    if (editStartDate.isAfter(editEndDate) || editStartDate.isSame(editEndDate)) {
      message.error('End date must be greater than start date.');
      return;
    }
    try {
      const token = await fetchToken();
      await axios.put(`${API_URL}/api/quarterinfo/${editingQuarter!.id}`, {
        startDate: editStartDate.format('YYYY-MM-DD'),
        endDate: editEndDate.format('YYYY-MM-DD'),
        modifiedBy: user_id,
      }, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      message.success(`Q${editingQuarter!.quarter} dates updated successfully.`);
      setIsEditModalVisible(false);
      fetchQuarters();
    } catch (error) {
      console.error('Error updating quarter:', error);
      message.error('Failed to update quarter dates.');
    }
  };

  const handleManualAllocate = async (record: QuarterInfo) => {
    confirm({
      title: `Manually allocate budget for Q${record.quarter}?`,
      icon: <ExclamationCircleOutlined />,
      content: 'This will immediately run the budget allocation for this quarter.',
      okText: 'Yes, Allocate',
      cancelText: 'Cancel',
      onOk: async () => {
        setAllocatingId(record.id);
        try {
          const token = await fetchToken();
          await axios.post(`${API_URL}/api/quarterinfo/manualAllocate/${record.id}`, {}, {
            headers: { Authorization: `Bearer ${token}` },
          });
          message.success(`Budget allocated successfully for Q${record.quarter}.`);
          fetchQuarters();
        } catch (error: any) {
          console.error('Error during manual allocation:', error);
          message.error(error?.response?.data || 'Failed to allocate budget.');
          fetchQuarters();
        } finally {
          setAllocatingId(null);
        }
      },
    });
  };

  const handleManualDeallocate = async (record: QuarterInfo) => {
    confirm({
      title: `Manually de-allocate budget for Q${record.quarter}?`,
      icon: <ExclamationCircleOutlined />,
      content: 'This will reset all manager budgets to 0 and return the balance to the main budget.',
      okText: 'Yes, De-allocate',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        setDeallocatingId(record.id);
        try {
          const token = await fetchToken();
          await axios.post(`${API_URL}/api/quarterinfo/manualDeallocate/${record.id}`, {}, {
            headers: { Authorization: `Bearer ${token}` },
          });
          message.success(`Budget de-allocated successfully for Q${record.quarter}.`);
          fetchQuarters();
        } catch (error: any) {
          console.error('Error during manual de-allocation:', error);
          message.error(error?.response?.data || 'Failed to de-allocate budget.');
          fetchQuarters();
        } finally {
          setDeallocatingId(null);
        }
      },
    });
  };

  const handleDeAllocateBudgetToggle = async (quarterId: string, currentValue: boolean) => {
    try {
      const token = await fetchToken();
      await axios.post(
        `${API_URL}/api/quarterinfo/updateDeAllocateBudget/${quarterId}`,
        {
          isDeAllocateBudget: !currentValue,
          modifiedBy: user_id,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      message.success(`Budget de-allocation ${!currentValue ? 'enabled' : 'disabled'} successfully.`);
      fetchQuarters();
    } catch (error) {
      console.error('Error updating de-allocate budget:', error);
      message.error('Failed to update budget de-allocation.');
    }
  };

  const columns = [
    {
      title: 'Quarter',
      dataIndex: 'quarter',
      key: 'quarter',
    },
    {
      title: 'Start Date',
      dataIndex: 'start_date',
      key: 'start_date',
      render: (text: string) => dayjs(text).format('DD-MMM-YYYY'),
    },
    {
      title: 'End Date',
      dataIndex: 'end_date',
      key: 'end_date',
      render: (text: string) => dayjs(text).format('DD-MMM-YYYY'),
    },
    {
      title: 'Allocate Budget',
      dataIndex: 'isAllocateBudget',
      key: 'isAllocateBudget',
      render: (value: boolean, record: QuarterInfo) => (
        <Switch
          checked={value}
          onChange={() => handleAllocateBudgetToggle(record.id, value)}
          checkedChildren="Yes"
          unCheckedChildren="No"
          size="small"
        />
      ),
    },
    {
      title: 'De-allocate Budget',
      dataIndex: 'isDeAllocateBudget',
      key: 'isDeAllocateBudget',
      render: (value: boolean, record: QuarterInfo) => (
        <Switch
          checked={value}
          onChange={() => handleDeAllocateBudgetToggle(record.id, value)}
          checkedChildren="Yes"
          unCheckedChildren="No"
          size="small"
        />
      ),
    },
    {
      title: 'Action',
      key: 'action',
      render: (text: any, record: QuarterInfo) => (
        <div style={{ display: 'flex', gap: 8 }}>
          <Button
            type="primary"
            onClick={() => handleManualAllocate(record)}
            size="small"
            loading={allocatingId === record.id}
            disabled={!!record.budget_allocated_at || !!allocatingId || !!deallocatingId || dayjs().isBefore(dayjs(record.start_date), 'day') || dayjs().isAfter(dayjs(record.end_date), 'day')}
            style={record.budget_allocated_at ? undefined : { backgroundColor: '#52C41A', borderColor: '#52C41A' }}
            title={record.budget_allocated_at ? `Allocated on ${dayjs(record.budget_allocated_at).format('DD-MMM-YYYY hh:mm A')}` : dayjs().isBefore(dayjs(record.start_date), 'day') ? `Available from ${dayjs(record.start_date).format('DD-MMM-YYYY')}` : dayjs().isAfter(dayjs(record.end_date), 'day') ? 'This quarter has ended' : ''}
          >
            {record.budget_allocated_at ? 'Allocated' : 'Allocate'}
          </Button>
          <Button
            type="primary"
            onClick={() => handleManualDeallocate(record)}
            size="small"
            loading={deallocatingId === record.id}
            disabled={!!record.budget_deallocated_at || (record.quarter !== 2 && record.quarter !== 4) || !!allocatingId || !!deallocatingId || dayjs().isBefore(dayjs(record.end_date), 'day')}
            style={{
              backgroundColor: !record.budget_deallocated_at && (record.quarter === 2 || record.quarter === 4) && !dayjs().isBefore(dayjs(record.end_date), 'day') ? '#faad14' : undefined,
              borderColor: !record.budget_deallocated_at && (record.quarter === 2 || record.quarter === 4) && !dayjs().isBefore(dayjs(record.end_date), 'day') ? '#faad14' : undefined,
            }}
            title={record.budget_deallocated_at ? `De-allocated on ${dayjs(record.budget_deallocated_at).format('DD-MMM-YYYY hh:mm A')}` : dayjs().isBefore(dayjs(record.end_date), 'day') ? `Available from ${dayjs(record.end_date).format('DD-MMM-YYYY')}` : ''}
          >
            {record.budget_deallocated_at ? 'De-allocated' : 'De-allocate'}
          </Button>
          <Button
            icon={<EditOutlined />}
            onClick={() => handleEditQuarter(record)}
            size="small"
          >
            Edit
          </Button>
          <Button
            type="primary"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDeleteQuarter(record.id)}
            size="small"
          >
            Delete
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div>
      <h4>People Manager Budget Automation</h4>
      <div style={{ marginBottom: 16 }}>
        <Button type="primary" onClick={() => setIsModalVisible(true)} style={{ marginRight: 8 }}>
          Add New Quarter
        </Button>
        {/* <Button type="primary" onClick={handleStartService} style={{ marginRight: 8 }}>
          Start Service
        </Button>
        <Button type="default" onClick={handleStopService}>
          Stop Service
        </Button> */}
      </div>
      <Table dataSource={quarters} columns={columns} rowKey="id" />

      <Modal
        title="Add New Quarter"
        visible={isModalVisible}
        onOk={handleAddQuarter}
        onCancel={handleCancelModal}
        okText="Save"
        afterClose={resetFormFields} // Ensure form is reset after modal is closed
      >
        <Divider />
        <Form layout="vertical">
          <Form.Item 
            label="Quarter Number" 
            required
            rules={[
              { required: true, message: 'Please select a quarter number!' },
              { 
                type: 'number', 
                min: 1, 
                max: 4, 
                message: 'Quarter number must be between 1 and 4!' 
              }
            ]}
          >
            <InputNumber
              min={1}
              max={4}
              step={1}
              precision={0}
              placeholder="Quarter Number (1-4)"
              onChange={(value) => {
                // Ensure only integers between 1-4 are accepted
                if (value && (value < 1 || value > 4 || !Number.isInteger(value))) {
                  return;
                }
                setQuarterNumber(value);
              }}
              style={{ width: '100%' }}
              value={quarterNumber}
              parser={(value) => {
                // Remove any non-numeric characters and parse to number
                const parsed = value ? value.replace(/[^\d]/g, '') : '';
                const num = parseInt(parsed, 10);
                // Only return valid quarter numbers (1-4), otherwise return NaN
                return (num >= 1 && num <= 4) ? num : NaN;
              }}
              formatter={(value) => {
                // Format to display only valid numbers
                return value ? value.toString() : '';
              }}
            />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item 
                label="Start Date" 
                required
                rules={[{ required: true, message: 'Please select a start date!' }]}
              >
                <DatePicker
                  onChange={(date) => {
                    setSelectedStartDate(date);
                    // Reset end date if it's on or before the new start date
                    if (selectedEndDate && date && (selectedEndDate.isBefore(date, 'day') || selectedEndDate.isSame(date, 'day'))) {
                      setSelectedEndDate(null);
                    }
                  }}
                  style={{ width: '100%' }}
                  placeholder="Select start date"
                  value={selectedStartDate}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item 
                label="End Date" 
                required
                rules={[{ required: true, message: 'Please select an end date!' }]}
              >
                <DatePicker
                  onChange={(date) => setSelectedEndDate(date)}
                  style={{ width: '100%' }}
                  placeholder="Select end date"
                  value={selectedEndDate}
                  disabledDate={(current) => {
                    // Disable dates that are on or before the selected start date
                    return selectedStartDate ? current && (current.isBefore(selectedStartDate, 'day') || current.isSame(selectedStartDate, 'day')) : false;
                  }}
                />
              </Form.Item>
            </Col>
          </Row>
          
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Allocate Budget">
                <Switch
                  checked={isAllocateBudget}
                  onChange={(checked) => setIsAllocateBudget(checked)}
                  checkedChildren="Yes"
                  unCheckedChildren="No"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="De-Allocate Budget">
                <Switch
                  checked={isDeAllocateBudget}
                  onChange={(checked) => setIsDeAllocateBudget(checked)}
                  checkedChildren="Yes"
                  unCheckedChildren="No"
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
      <Modal
        title={`Edit Q${editingQuarter?.quarter} Dates`}
        open={isEditModalVisible}
        onOk={handleEditSave}
        onCancel={() => setIsEditModalVisible(false)}
        okText="Save"
      >
        <Divider />
        <Form layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Start Date" required>
                <DatePicker
                  value={editStartDate}
                  onChange={(date) => {
                    setEditStartDate(date);
                    if (editEndDate && date && (editEndDate.isBefore(date, 'day') || editEndDate.isSame(date, 'day'))) {
                      setEditEndDate(null);
                    }
                  }}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="End Date" required>
                <DatePicker
                  value={editEndDate}
                  onChange={(date) => setEditEndDate(date)}
                  style={{ width: '100%' }}
                  disabledDate={(current) =>
                    editStartDate ? current && (current.isBefore(editStartDate, 'day') || current.isSame(editStartDate, 'day')) : false
                  }
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default PeopleManagerBudgetAutomation;